/**
 * AVBP Staff-Roles admin model — the role-assignment layer.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY (Settings → Staff Roles, owner-asked before 26 May)
 * ─────────────────────────────────────────────────────────────────────────
 *  The locked auth model mints AVBP custom claims from HR's `staff/`
 *  collection at sign-in (`resolveTeamClaimsOnSignIn` →
 *  `buildTeamClaims` reads `staff.roles[]`). There was no in-portal way to
 *  change a staffer's AVBP role — so granting Khilendra a leads-capable /
 *  admin-capable role required hand-editing Firestore. This module is the
 *  pure model + the thin Firestore seam an admin-only Settings screen uses
 *  to flip `staff/{id}.roles`.
 *
 *  RULES CEILING (no firestore.rules change — HR-shared file):
 *    match /staff/{staffId} {
 *      allow get, list: if true;
 *      allow create, delete: if isHrRole();           // admin|hr
 *      allow update: if isHrRole() || (self pin-only) // admin|hr
 *    }
 *  `isHrRole()` = hasRoleAny(['admin','hr']). An ADMIN session therefore has
 *  full update on staff/{id} including the `roles` array. No rules change.
 *
 *  PROPAGATION: claims are minted from staff/ ONLY at sign-in by
 *  `resolveTeamClaimsOnSignIn` + a forced token refresh (auth.tsx). A role
 *  change here writes staff/{id}.roles; it takes effect on that staffer's
 *  NEXT sign-in / token refresh — NOT live. The UI states this explicitly.
 *
 *  PURE/IMPURE split (mirrors projectStore.ts): `staffRolePatchBody`,
 *  `normaliseRoles`, `permissionsForRoles`, `staffRowFrom` are PURE +
 *  unit-tested. Only `listenAvbpStaff` / `updateStaffRoles` touch Firestore.
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Role } from './roles';

/* ════════════════════ Assignable AVBP role set ════════════════════ */

/**
 * The roles an admin may assign in this screen. This is the locked LOGIN
 * role set (roles.ts `Role`) MINUS `client` — a client is "not in the
 * staff master", never an assignable staff role. `roleFromClaims` consumes
 * `staff.roles[]`: 'admin' wins; otherwise the first entry resolves the
 * track. So a single-role array is the meaningful unit; we keep it an
 * array because that is the on-disk + claim shape (`buildTeamClaims`).
 */
export const ASSIGNABLE_ROLES: Exclude<Role, 'client'>[] = [
  'admin',
  'staff',
  'hr',
  'partner',
  'external_consultant',
];

export interface RoleOption {
  role: Exclude<Role, 'client'>;
  label: string;
  /** one-line description of what this role can do in AVBP today. */
  blurb: string;
}

/** Human labels + the real, current AVBP capability of each role. */
export const ROLE_OPTIONS: RoleOption[] = [
  {
    role: 'admin',
    label: 'Admin',
    blurb:
      'Full portal: Lead Inbox, all Clients/Projects, Money, Workload, ' +
      'lead-identity edit, gate/quote commit, Settings (this screen).',
  },
  {
    role: 'staff',
    label: 'Staff',
    blurb:
      'Lead Inbox + own assigned Projects + Conversation. Can commit on ' +
      'own projects. No Money/Workload, no lead-identity edit, no Settings.',
  },
  {
    role: 'hr',
    label: 'HR',
    blurb:
      'HR login role (dedicated AVBP screens are a later sprint — resolves ' +
      'to the placeholder today). Can write the staff directory in HR app.',
  },
  {
    role: 'partner',
    label: 'Partner',
    blurb:
      'Channel-partner login role (dedicated screens later sprint — ' +
      'placeholder + Conversation only today).',
  },
  {
    role: 'external_consultant',
    label: 'External consultant',
    blurb:
      'Scoped external professional login role (dedicated screens later ' +
      'sprint — placeholder + Conversation only today).',
  },
];

/* ════════════════════ Permission preview (real screens) ════════════════════
 *
 *  AVBP today has COARSE login roles, not a granular permission grid (cf.
 *  the HR app's `ALL_PERMISSIONS` 30-key matrix). Rather than fake a grid
 *  the role doesn't actually consume, this derives the *real* capability of
 *  a role from the same predicates the app gates on (roles.ts visibilityFor
 *  + the NAV map + the App.tsx route guards). It is a faithful PREVIEW so
 *  the owner sees exactly what assigning a role grants — and it is shaped
 *  as a keyed list so finer per-permission flags can be layered later
 *  WITHOUT changing the call sites (same structure the HR grid uses).
 */

export interface AvbpPermission {
  key: string;
  label: string;
  group: string;
  /**
   * true = this key is NEVER implied by a role — not even admin. It is held
   * only via an explicit per-person grant on `staff/{id}.capabilityGrants[]`.
   * `permissionsForRoles` therefore excludes it from the admin blanket, so
   * the Settings preview cannot promise something the backend won't honour.
   */
  grantOnly?: boolean;
}

/**
 * The AVBP capability vocabulary.
 *
 * SSOT: `spec/avbp-capabilities.json` — this array MIRRORS it (key, label,
 * group, grantOnly). `test/capabilityRegistry.test.cjs` fails if the two
 * drift, or if the Cloud-Functions mirror (`functions/functions/lib/
 * capabilities.js` → CAPABILITY_CATALOG) drifts from either. Edit the JSON
 * first, then both mirrors, in one commit.
 *
 * (Historic note: comments here used to point at `spec/avbp-spec.json` as
 * the canonical capability catalog. That file exists but is the 2026-05
 * full-scope DESIGN spec on a different naming scheme — it was never the
 * runtime catalog. `spec/avbp-capabilities.json` is.)
 */
export const AVBP_PERMISSIONS: AvbpPermission[] = [
  { key: 'leads.view', label: 'Lead Inbox access', group: 'Leads' },
  { key: 'leads.edit_identity', label: 'Edit lead identity (name/phone/email)', group: 'Leads' },
  { key: 'clients.view', label: 'Clients / Projects list', group: 'Clients' },
  { key: 'clients.edit', label: 'Edit client / project records', group: 'Clients' },
  { key: 'project.commit', label: 'Commit a gate / approve a proposal', group: 'Clients' },
  { key: 'money.view', label: 'Money (payments, gates, ledger amounts)', group: 'Money' },
  { key: 'workload.view', label: 'Workload (cross-project oversight)', group: 'Money' },
  // ── Phase 1 Task C (SC-058) — admin money-gate override + reminder ──
  // money.gate.override is admin-ONLY and not grantable; the audit entry is
  // a system obligation, not a user action. payments.markPaid.manual is the
  // staff/admin offline-payment action. Both are registered here so they
  // surface in Settings → Roles & Rights (capability framework lock).
  { key: 'money.gate.override', label: 'Override a money gate (admin — release before payment)', group: 'Money' },
  { key: 'payments.markPaid.manual', label: 'Mark an offline payment received (cash/bank)', group: 'Money' },
  { key: 'admin.queue', label: 'Admin action queue (away-mode review)', group: 'Admin' },
  { key: 'settings.staff_roles', label: 'Settings → assign staff roles', group: 'Admin' },
  // ── Build B (2026-05-21) — task-oversight visibility ──
  // The AI assistant (in-app + WhatsApp) creates real `tasks/` records.
  // `tasks.view.all` is the admin cross-team oversight screen (All Tasks,
  // by-assignee). `tasks.view.own` is every team member's own task list
  // (the Home "Your tasks" panel). Registered in the capability framework
  // so they surface in Settings → Roles & Rights.
  { key: 'tasks.view.all', label: 'All Tasks — cross-team oversight (every assignee)', group: 'Clients' },
  { key: 'tasks.view.own', label: 'My tasks (own assigned tasks on Home)', group: 'Clients' },
  // ── Recoverable Trash (2026-05-22) — owner decision ──
  // Deleted leads / clients / projects go to a recoverable Trash, never a
  // hard delete. `trash.manage` covers the destructive-but-reversible
  // move-to-Trash / restore actions + the Trash view; it DEFAULTS to admin
  // and the owner may grant it to the Admin Assistant role via the Roles
  // matrix. `trash.purge` is the IRREVERSIBLE permanent delete (Empty Trash
  // / Delete permanently) — admin-only, NOT grantable. `clients.merge`
  // merges a duplicate client into another — admin-only (it re-points
  // every project + money history). Staff park/reject is unchanged and
  // needs none of these. Keys mirror the capability-framework lock —
  // every new feature registers its key in the same commit.
  { key: 'trash.manage', label: 'Move to / restore from Trash · view the Trash', group: 'Admin' },
  { key: 'trash.purge', label: 'Permanently delete from Trash (Empty Trash — admin, irreversible)', group: 'Admin' },
  { key: 'clients.merge', label: 'Merge a duplicate client into another (admin)', group: 'Clients' },
  // ── WA Team Inbox (Phase 2, 2026-06-12) — teaminbox.* keys ──
  // Runtime check = functions/lib/teaminbox.js (defaults ∪ grants − revokes,
  // grant/revoke arrays on the staff doc). Registered here per the locked
  // capability framework so Roles & Rights previews them.
  { key: 'teaminbox.view', label: 'WhatsApp Team Inbox — view conversations', group: 'Messages' },
  { key: 'teaminbox.send', label: 'WhatsApp Team Inbox — send replies', group: 'Messages' },
  { key: 'teaminbox.assign', label: 'WhatsApp Team Inbox — assign chats (staff: to self only)', group: 'Messages' },
  { key: 'teaminbox.note', label: 'WhatsApp Team Inbox — internal notes', group: 'Messages' },
  { key: 'teaminbox.settings', label: 'WhatsApp Inbox Settings (S6 — numbers, pools, canned)', group: 'Messages' },
  // ── L1C commercial path (2026-06-23, L1C_L2C_AVBP_INTEGRATION §7) ──
  // Capability keys registered in the same commit as the feature (capability-
  // framework lock). These are feature-registration keys surfaced in Roles &
  // Rights; the Setu credit is computed server-side (CF) regardless of role.
  { key: 'quote.tier.L1C', label: 'Quote the L1C Review Path · Sameeksha (plan review)', group: 'Money' },
  { key: 'billing.credit.setu', label: 'Setu credit (L1C → L2x) — system-computed credit line', group: 'Money' },
  { key: 'report.template.L1C', label: 'L1C plan-review verdict report template', group: 'Clients' },
  // ── AI assistant · Milestone A send hands (2026-07-21) ──
  // Runtime check = functions/lib/capabilities.js (the shared registry the
  // teaminbox resolver now also uses). GRANT-ONLY: no role holds these, not
  // even admin — each is ticked person by person here and takes effect on
  // that staffer's next sign-in. Day-one holders: Rohit (all five), Himani
  // (assistant.book.consult only).
  { key: 'assistant.send.wa', label: 'AI assistant — send an approved WhatsApp template (per person, confirm-first)', group: 'Assistant', grantOnly: true },
  { key: 'assistant.send.email', label: 'AI assistant — send an email to a client (per person, confirm-first)', group: 'Assistant', grantOnly: true },
  { key: 'assistant.send.quote', label: 'AI assistant — send a quote + pay link (per person, confirm-first)', group: 'Assistant', grantOnly: true },
  { key: 'assistant.send.intake', label: 'AI assistant — send the intake form link (per person, confirm-first)', group: 'Assistant', grantOnly: true },
  { key: 'assistant.book.consult', label: 'AI assistant — read calendar availability and book a consult (per person, confirm-first)', group: 'Assistant', grantOnly: true },
];

/**
 * PURE — the permission keys a role set actually grants TODAY, derived from
 * the live gates (App.tsx route `roles=[...]` + roles.ts visibilityFor +
 * the lead-identity admin-only edit). `admin` in the set wins (matches
 * roleFromClaims). Returned as a Set of keys from AVBP_PERMISSIONS.
 */
export function permissionsForRoles(roles: string[]): Set<string> {
  const r = roles.map((x) => String(x).toLowerCase());
  const isAdmin = r.includes('admin');
  // Any non-admin team member resolves to the staff track today
  // (roleFromClaims): hr/partner/external_consultant land on the
  // placeholder, so their *effective* AVBP capability is the safe floor.
  const isStaff = !isAdmin && r.includes('staff');

  const grant = new Set<string>();
  if (isAdmin) {
    // grantOnly keys are excluded on purpose: the backend resolver hands
    // them out to NO role (functions/lib/capabilities.js roleDefaults=[]),
    // so showing them as an admin default would be a promise the send-doors
    // would refuse. They light up once the person is granted the key.
    for (const p of AVBP_PERMISSIONS) if (!p.grantOnly) grant.add(p.key);
    return grant;
  }
  if (isStaff) {
    grant.add('leads.view');
    grant.add('clients.view');
    grant.add('clients.edit'); // isTeam update on projects/clients (rules)
    grant.add('project.commit'); // only own assigned projects (canCommit)
    // Task C: staff CAN record an offline payment (markPaidManual CF is
    // admin+staff). They CANNOT override a money gate — that is admin-only.
    grant.add('payments.markPaid.manual');
    // Build B: staff see their OWN tasks (the Home "Your tasks" panel).
    // They do NOT get tasks.view.all — the cross-team All Tasks oversight
    // screen is admin-only (route-gated in App.tsx).
    grant.add('tasks.view.own');
    // WA Team Inbox (Phase 2): staff default = view/send/note + assign-to-self.
    // NO teaminbox.settings (S6 is admin-only by default).
    grant.add('teaminbox.view');
    grant.add('teaminbox.send');
    grant.add('teaminbox.note');
    grant.add('teaminbox.assign');
    // NO money.view / workload.view / money.gate.override / admin.queue /
    // settings / lead-identity / tasks.view.all.
  }
  // hr/partner/external_consultant (no admin/staff) → no AVBP track yet:
  // empty set is correct (they hit the placeholder).
  return grant;
}

/* ════════════════════ Staff row model ════════════════════ */

export interface AvbpStaffRow {
  /** staff/ doc id (e.g. E016). */
  id: string;
  name: string;
  email: string;
  /** the on-disk staff.roles[] array (claim source). */
  roles: string[];
  surfaces: string[];
  firmId: string | null;
  active: boolean;
}

/**
 * PURE — map a raw staff/ doc to an AvbpStaffRow. `active` accepts either
 * `active` or `isActive` (avbp_auth_oauth resolveOAuthVerdict accepts
 * both). Returns null for docs WITHOUT the 'avbp' surface — the screen
 * only manages people who can actually sign into AVBP.
 */
export function staffRowFrom(
  id: string,
  d: Record<string, unknown>,
): AvbpStaffRow | null {
  const surfaces = Array.isArray(d.surfaces)
    ? (d.surfaces as unknown[]).map((s) => String(s))
    : [];
  if (!surfaces.includes('avbp')) return null;
  const roles = Array.isArray(d.roles)
    ? (d.roles as unknown[]).map((x) => String(x))
    : [];
  return {
    id,
    name: (d.name as string) ?? id,
    email: (d.email as string) ?? '',
    roles,
    surfaces,
    firmId: (d.firmId as string) ?? null,
    active: d.active === true || d.isActive === true,
  };
}

/**
 * PURE — normalise a desired role selection to the on-disk array shape
 * `buildTeamClaims` expects. We assign exactly ONE primary role (the model
 * roleFromClaims actually resolves on); the array is de-duped, lower-cased,
 * and validated against ASSIGNABLE_ROLES. Throws on an unknown/empty role
 * so a bad write can never reach Firestore.
 */
export function normaliseRoles(desired: string[]): string[] {
  const cleaned = Array.from(
    new Set(
      desired
        .map((x) => String(x).toLowerCase().trim())
        .filter(Boolean),
    ),
  );
  const allowed = ASSIGNABLE_ROLES as string[];
  for (const r of cleaned) {
    if (!allowed.includes(r)) {
      throw new Error(`Not an assignable AVBP role: "${r}"`);
    }
  }
  if (cleaned.length === 0) {
    throw new Error('At least one role must be assigned.');
  }
  // 'admin' is authoritative — if present, keep it first (mirrors
  // buildTeamClaims legacyRole + roleFromClaims precedence).
  cleaned.sort((a, b) => (a === 'admin' ? -1 : b === 'admin' ? 1 : 0));
  return cleaned;
}

/**
 * PURE — the EXACT doc body an updateStaffRoles write sends. ONLY the
 * `roles` array + an audit stamp. No other staff field is ever touched
 * (so this write can never disturb pinHash/salt/email/surfaces — it stays
 * minimal and rules-safe). `roles` is normalised (throws on bad input).
 * `updatedAt` is filled with serverTimestamp by the impure caller.
 */
export function staffRolePatchBody(
  desired: string[],
  actorName: string,
): Record<string, unknown> {
  return {
    roles: normaliseRoles(desired),
    avbpRolesUpdatedBy: actorName,
  };
}

/* ════════════════════ Firestore seam (impure) ════════════════════ */

/**
 * Live realtime list of AVBP-surface staff (staff/ where surfaces⊇'avbp').
 * `get, list` is rules-open, so this works for any signed-in user — but
 * the Settings ROUTE is admin-gated (App.tsx), so only an admin reaches it.
 */
export function listenAvbpStaff(
  cb: (rows: AvbpStaffRow[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'staff')),
    (snap) => {
      const rows: AvbpStaffRow[] = [];
      for (const dd of snap.docs) {
        const r = staffRowFrom(dd.id, dd.data());
        if (r) rows.push(r);
      }
      rows.sort((a, b) => (a.id < b.id ? -1 : 1));
      cb(rows);
    },
    (e) => onErr?.(e),
  );
}

/**
 * Write a new role set to staff/{id}.roles. Rules: an ADMIN session
 * satisfies `isHrRole()` → full update on staff/{id}, so this needs NO
 * rules change. The write is intentionally MINIMAL (roles + audit stamp)
 * so it never collides with the self-update pin-only whitelist branch and
 * never disturbs any other staff field.
 *
 * IMPORTANT: claims are NOT re-minted here. They are minted from staff/ at
 * the staffer's NEXT sign-in (resolveTeamClaimsOnSignIn + token refresh).
 * Callers MUST surface "applies on next login".
 */
export async function updateStaffRoles(
  staffId: string,
  desired: string[],
  actorName: string,
): Promise<void> {
  await updateDoc(doc(db, 'staff', staffId), {
    ...staffRolePatchBody(desired, actorName),
    avbpRolesUpdatedAt: serverTimestamp(),
  });
}
